import { useResizeObserverCallback } from '@databases/react';
import composeRefs from '@seznam/compose-react-refs';
import React, { forwardRef, ReactNode, Ref, useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-left: 1px;
  height: 100%;
`;

const overheadSpaceStyle = css`
  margin-top: -8px;
  padding-top: 8px;
`;

const InnerContainer = styled.div<{
  allowOverheadSpace?: boolean;
  heightIsLessThanContainer?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-x: auto;
  overflow-y: ${(props) => (props.heightIsLessThanContainer ? 'hidden' : 'auto')};
  margin-right: 1px;
  // Extra space for column selectors on DatabaseContent
  ${(props) => props.allowOverheadSpace && overheadSpaceStyle}
`;

export interface ScrollableContainerWithShadowProps {
  className?: string;
  children: ReactNode;
  innerRef?: Ref<HTMLDivElement>;
  heightIsLessThanContainer?: boolean;
  allowOverheadSpace?: boolean;
  hasCalulations: boolean;
}

const ScrollableContainer = forwardRef<
  HTMLDivElement,
  ScrollableContainerWithShadowProps
>(function ScrollableContainer(
  { className, children, innerRef, heightIsLessThanContainer, allowOverheadSpace },
  ref
) {
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const shadowContainerRef = useRef<HTMLDivElement>(null);
  const topShadowRef = useRef<HTMLDivElement>(null);
  const bottomShadowRef = useRef<HTMLDivElement>(null);

  const update = useCallback(() => {
    if (
      outerContainerRef.current &&
      innerContainerRef.current &&
      topShadowRef.current &&
      bottomShadowRef.current
    ) {
      topShadowRef.current.style.opacity =
        innerContainerRef.current.scrollTop >= 1 ? '1' : '0';
      bottomShadowRef.current.style.opacity =
        Math.abs(
          innerContainerRef.current.scrollTop +
            innerContainerRef.current.clientHeight -
            innerContainerRef.current.scrollHeight
        ) >= 1
          ? '1'
          : '0';
    }
  }, []);

  const handleResize = useCallback(() => {
    if (shadowContainerRef.current && innerContainerRef.current) {
      // Make sure that the shadow container does not cover the scroll bars
      shadowContainerRef.current.style.bottom = `${
        innerContainerRef.current.offsetHeight - innerContainerRef.current.clientHeight
      }px`;
      shadowContainerRef.current.style.right = `${
        innerContainerRef.current.offsetWidth - innerContainerRef.current.clientWidth
      }px`;
    }
    update();
  }, [update]);

  useResizeObserverCallback(innerContainerRef.current, handleResize);

  useEffect(() => {
    const ref = innerContainerRef.current;
    if (ref) {
      ref.addEventListener('scroll', update);
      return () => {
        ref.removeEventListener('scroll', update);
      };
    }
  }, [update]);

  return (
    <OuterContainer
      data-testid="scrollable-outer-container"
      ref={composeRefs(outerContainerRef, ref)}
      className={className}
    >
      <InnerContainer
        id={'scrollable-inner-container'}
        data-testid="scrollable-inner-container"
        allowOverheadSpace={allowOverheadSpace}
        ref={composeRefs(innerContainerRef, innerRef)}
        heightIsLessThanContainer={heightIsLessThanContainer}
      >
        {children}
      </InnerContainer>
    </OuterContainer>
  );
});

export default ScrollableContainer;
